
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Trophy, Plus, Star, Upload, CheckCircle, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Skill {
  id: string;
  name: string;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  verified: boolean;
  certificates: Certificate[];
  projects: Project[];
  endorsements: number;
}

interface Certificate {
  id: string;
  title: string;
  issuer: string;
  date: string;
  url?: string;
  verified: boolean;
}

interface Project {
  id: string;
  title: string;
  description: string;
  technologies: string[];
  url?: string;
  featured: boolean;
}

const VerifiedSkillPortfolio: React.FC = () => {
  const { toast } = useToast();
  const [skills, setSkills] = useState<Skill[]>([
    {
      id: '1',
      name: 'JavaScript',
      category: 'Programming',
      level: 'Advanced',
      verified: true,
      certificates: [
        {
          id: '1',
          title: 'JavaScript Fundamentals',
          issuer: 'freeCodeCamp',
          date: '2024-01-15',
          url: 'https://freecodecamp.org/certification',
          verified: true
        }
      ],
      projects: [
        {
          id: '1',
          title: 'Weather App',
          description: 'A responsive weather application using React and OpenWeather API',
          technologies: ['React', 'JavaScript', 'CSS'],
          url: 'https://github.com/user/weather-app',
          featured: true
        }
      ],
      endorsements: 8
    },
    {
      id: '2',
      name: 'Python',
      category: 'Programming',
      level: 'Intermediate',
      verified: false,
      certificates: [],
      projects: [
        {
          id: '2',
          title: 'Data Analysis Project',
          description: 'Analysis of student performance using pandas and matplotlib',
          technologies: ['Python', 'Pandas', 'Matplotlib'],
          featured: false
        }
      ],
      endorsements: 3
    }
  ]);

  const [newSkill, setNewSkill] = useState({
    name: '',
    category: '',
    level: 'Beginner' as const
  });

  const [selectedTab, setSelectedTab] = useState<'skills' | 'certificates' | 'projects'>('skills');

  const skillCategories = ['Programming', 'Design', 'Marketing', 'Business', 'Data Science', 'Other'];
  const skillLevels: Array<'Beginner' | 'Intermediate' | 'Advanced' | 'Expert'> = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

  const addSkill = () => {
    if (!newSkill.name || !newSkill.category) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    const skill: Skill = {
      id: Date.now().toString(),
      ...newSkill,
      verified: false,
      certificates: [],
      projects: [],
      endorsements: 0
    };

    setSkills([...skills, skill]);
    setNewSkill({ name: '', category: '', level: 'Beginner' });
    toast({
      title: "Skill Added!",
      description: "Your skill has been added to your portfolio."
    });
  };

  const requestVerification = (skillId: string) => {
    setSkills(skills.map(skill => 
      skill.id === skillId ? { ...skill, verified: true } : skill
    ));
    toast({
      title: "Verification Requested",
      description: "Your skill verification request has been submitted."
    });
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner': return 'bg-blue-100 text-blue-800';
      case 'Intermediate': return 'bg-green-100 text-green-800';
      case 'Advanced': return 'bg-orange-100 text-orange-800';
      case 'Expert': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-green-500" />
            Verified Skill Portfolio Builder
          </CardTitle>
          <CardDescription>
            Build and showcase your verified skills with certificates, projects, and endorsements.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex space-x-2 border-b">
            <button
              onClick={() => setSelectedTab('skills')}
              className={`pb-2 px-1 ${selectedTab === 'skills' ? 'border-b-2 border-green-500 text-green-600' : 'text-muted-foreground'}`}
            >
              Skills Overview
            </button>
            <button
              onClick={() => setSelectedTab('certificates')}
              className={`pb-2 px-1 ${selectedTab === 'certificates' ? 'border-b-2 border-green-500 text-green-600' : 'text-muted-foreground'}`}
            >
              Certificates
            </button>
            <button
              onClick={() => setSelectedTab('projects')}
              className={`pb-2 px-1 ${selectedTab === 'projects' ? 'border-b-2 border-green-500 text-green-600' : 'text-muted-foreground'}`}
            >
              Projects
            </button>
          </div>

          {/* Skills Overview Tab */}
          {selectedTab === 'skills' && (
            <div className="space-y-6">
              {/* Add New Skill */}
              <Card className="bg-green-50">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3">Add New Skill</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Input
                      placeholder="Skill name"
                      value={newSkill.name}
                      onChange={(e) => setNewSkill({...newSkill, name: e.target.value})}
                    />
                    <select
                      value={newSkill.category}
                      onChange={(e) => setNewSkill({...newSkill, category: e.target.value})}
                      className="p-2 border rounded-md"
                    >
                      <option value="">Select Category</option>
                      {skillCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <select
                      value={newSkill.level}
                      onChange={(e) => setNewSkill({...newSkill, level: e.target.value as any})}
                      className="p-2 border rounded-md"
                    >
                      {skillLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                    <Button onClick={addSkill} className="bg-green-500 hover:bg-green-600">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Skill
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Skills Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {skills.map((skill) => (
                  <Card key={skill.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold flex items-center gap-2">
                            {skill.name}
                            {skill.verified && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </h4>
                          <p className="text-sm text-muted-foreground">{skill.category}</p>
                        </div>
                        <Badge className={getLevelColor(skill.level)}>
                          {skill.level}
                        </Badge>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span>Certificates:</span>
                          <span>{skill.certificates.length}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Projects:</span>
                          <span>{skill.projects.length}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Endorsements:</span>
                          <span className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-yellow-500" />
                            {skill.endorsements}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {!skill.verified && (
                          <Button 
                            size="sm"
                            onClick={() => requestVerification(skill.id)}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verify
                          </Button>
                        )}
                        <Button size="sm" variant="outline">
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Certificates Tab */}
          {selectedTab === 'certificates' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">My Certificates</h3>
                <Button className="bg-green-500 hover:bg-green-600">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Certificate
                </Button>
              </div>

              {skills.flatMap(skill => skill.certificates).map((cert) => (
                <Card key={cert.id} className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          {cert.title}
                          {cert.verified && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </h4>
                        <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                        <p className="text-sm text-muted-foreground">{cert.date}</p>
                      </div>
                      <div className="flex gap-2">
                        {cert.url && (
                          <Button size="sm" variant="outline">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        )}
                        <Button size="sm" variant="outline">
                          Share
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Projects Tab */}
          {selectedTab === 'projects' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">My Projects</h3>
                <Button className="bg-green-500 hover:bg-green-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Project
                </Button>
              </div>

              {skills.flatMap(skill => skill.projects).map((project) => (
                <Card key={project.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          {project.title}
                          {project.featured && (
                            <Star className="h-4 w-4 text-yellow-500" />
                          )}
                        </h4>
                        <p className="text-sm text-muted-foreground mb-2">{project.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {project.technologies?.map((tech, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {project.url && (
                        <Button size="sm" variant="outline">
                          <ExternalLink className="h-3 w-3 mr-1" />
                          View Project
                        </Button>
                      )}
                      <Button size="sm" variant="outline">
                        Edit
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifiedSkillPortfolio;
